<template>
  <v-container fluid class="questionarios-disponiveis">
    <v-row justify="center">
      <v-col cols="12" md="10" lg="8">
        <!-- Header -->
        <v-card class="header-card elevation-4 mb-4">
          <v-card-text class="d-flex align-center pa-4">
            <v-icon color="primary" size="32" class="mr-3">mdi-clipboard-list</v-icon>
            <div>
              <h2 class="primary--text mb-1">Questionários Disponíveis</h2>
              <p class="text-caption grey--text mb-0">Selecione um questionário para responder</p>
            </div>
          </v-card-text>
        </v-card>

        <!-- Back Button -->
        <v-card class="back-card elevation-2 mb-4 cursor-pointer" @click="voltarParaLogin">
          <v-card-text class="d-flex align-center pa-3">
            <v-icon color="primary" class="mr-2">mdi-arrow-left</v-icon>
            <span class="primary--text font-weight-medium">Voltar</span>
          </v-card-text>
        </v-card>

        <!-- Lista de Questionários -->
        <div class="questionarios-lista">
          <v-card
            v-for="(item, index) in disponivel"
            :key="`questionario-${index}`"
            class="questionario-card elevation-4 mb-4"
            :class="{ respondido: item.respondido == 1 }"
          >
            <!-- Header do Card -->
            <div class="card-header">
              <div class="d-flex align-center">
                <v-avatar :color="item.respondido == 1 ? 'success' : 'primary'" size="40" class="mr-3">
                  <v-icon color="white">
                    {{ item.respondido == 1 ? "mdi-check-circle" : "mdi-clipboard-text" }}
                  </v-icon>
                </v-avatar>
                <div>
                  <h3 class="white--text mb-0">{{ item.nome }}</h3>
                  <p class="white--text text-caption mb-0 opacity-8">ID: {{ item.id_questionario }}</p>
                </div>
              </div>
              <v-chip
                :color="item.respondido == 1 ? 'success' : 'warning'"
                :text-color="item.respondido == 1 ? 'white' : 'black'"
                small
              >
                {{ item.respondido == 1 ? "Respondido" : "Pendente" }}
              </v-chip>
            </div>

            <!-- Conteúdo do Card -->
            <v-card-text class="pa-4">
              <v-container fluid>
                <v-row>
                  <v-col cols="12" md="8">
                    <!-- Informações Pessoais -->
                    <div class="info-section mb-3">
                      <h4 class="section-title">
                        <v-icon color="primary" class="mr-1">mdi-account</v-icon>
                        Informações Pessoais
                      </h4>
                      <div class="info-grid">
                        <div class="info-item">
                          <span class="info-label">Nome:</span>
                          <span class="info-value">{{ item.nome }}</span>
                        </div>
                        <div class="info-item" v-if="item.aluno">
                          <span class="info-label">CGM:</span>
                          <span class="info-value">{{ item.cgm }}</span>
                        </div>
                        <div class="info-item" v-if="item.cpf">
                          <span class="info-label">CPF:</span>
                          <span class="info-value">{{ formatCpf(item.cpf) }}</span>
                        </div>
                      </div>
                    </div>

                    <!-- Informações da Instituição -->
                    <div class="info-section">
                      <h4 class="section-title">
                        <v-icon color="primary" class="mr-1">mdi-school</v-icon>
                        Instituição
                      </h4>
                      <div class="info-grid">
                        <div class="info-item">
                          <span class="info-label">Nome:</span>
                          <span class="info-value font-weight-medium">{{ item.instituicao }}</span>
                        </div>
                        <div class="info-item">
                          <span class="info-label">ID:</span>
                          <span class="info-value">{{ item.id_instituicao }}</span>
                        </div>
                      </div>
                    </div>
                  </v-col>

                  <v-col cols="12" md="4">
                    <!-- Status e Ações -->
                    <div class="action-section">
                      <div class="status-card" :class="item.respondido == 1 ? 'success' : 'warning'">
                        <v-icon :color="item.respondido == 1 ? 'success' : 'warning'" size="24">
                          {{ item.respondido == 1 ? "mdi-check-circle" : "mdi-clock-outline" }}
                        </v-icon>
                        <div class="status-text">
                          <strong>{{ item.respondido == 1 ? "Concluído" : "Pendente" }}</strong>
                          <small>
                            {{ item.respondido == 1 ? "Questionário já respondido" : "Aguardando resposta" }}
                          </small>
                        </div>
                      </div>

                      <!-- Botão de Ação -->
                      <v-btn
                        v-if="item.respondido == 0"
                        block
                        color="success"
                        class="action-btn mt-3"
                        large
                        elevation="2"
                        @click="responderQuestionario(item)"
                      >
                        <v-icon left>mdi-play-circle</v-icon>
                        Responder Agora
                      </v-btn>

                      <v-btn v-else block color="grey" class="action-btn mt-3" large disabled elevation="0">
                        <v-icon left>mdi-check</v-icon>
                        Já Respondido
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>
          </v-card>
        </div>

        <!-- Estado Vazio -->
        <v-card v-if="!disponivel || disponivel.length === 0" class="empty-state elevation-4">
          <v-card-text class="text-center pa-8">
            <v-icon color="grey" size="64">mdi-clipboard-text-off</v-icon>
            <h3 class="grey--text mt-4 mb-2">Nenhum questionário disponível</h3>
            <p class="grey--text">Não há questionários pendentes para responder no momento.</p>
            <v-btn color="primary" @click="voltarParaLogin" class="mt-4">
              <v-icon left>mdi-arrow-left</v-icon>
              Voltar ao Início
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapGetters, mapMutations } from "vuex";

export default {
  name: "QuestionariosDisponiveis",
  computed: {
    ...mapGetters({ param: "avaliacao/param" }),
    disponivel() {
      return this.param && this.param.disponivel ? this.param.disponivel : [];
    },
  },
  methods: {
    ...mapMutations({ setPessoa: "avaliacao/setPessoa" }),
    formatCpf(cpf) {
      if (!cpf) return "";
      const numbers = cpf.replace(/\D/g, "");
      if (numbers.length === 11) {
        return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
      }
      return cpf;
    },

    voltarParaLogin() {
      // Limpar dados disponíveis ao voltar
      this.param.disponivel = [];
      this.$router.push("/login");
    },

    responderQuestionario(item) {
      // Adaptar o objeto para o formato esperado pelo sistema

      const adaptedItem = {
        nome: item.nome,
        instituicao: item.instituicao,
        id_instituicao: item.id_instituicao,
        id_questionario: item.id_questionario,

        cgm: item.cgm,
        cpf: item.cpf,
        avaliado: item.respondido,
        apto: 1,
      };
      console.log(this.param);
      this.setPessoa(adaptedItem);
      this.$router.push("/questionario");
    },
  },
};
</script>

<style scoped>
.questionarios-disponiveis {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
  padding: 20px 0 64px 0; /* padding-bottom extra para evitar sobreposição do footer */
}

.header-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 12px;
}

.back-card {
  border-radius: 8px;
  transition: all 0.3s ease;
  cursor: pointer;
}

.back-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.questionario-card {
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
  animation: slideInUp 0.5s ease;
}

.questionario-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
}

.questionario-card.respondido {
  border-left: 4px solid #4caf50;
}

.card-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 16px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.info-section {
  border-left: 3px solid #e3f2fd;
  padding-left: 12px;
  margin-bottom: 16px;
}

.section-title {
  color: #1976d2;
  font-size: 16px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
}

.info-grid {
  display: grid;
  gap: 8px;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-label {
  font-weight: 500;
  color: #666;
  min-width: 60px;
}

.info-value {
  color: #333;
  flex: 1;
}

.action-section {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.status-card {
  padding: 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 12px;
  border: 2px solid;
}

.status-card.success {
  background: #e8f5e8;
  border-color: #4caf50;
}

.status-card.warning {
  background: #fff3e0;
  border-color: #ff9800;
}

.status-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.status-text strong {
  font-size: 14px;
}

.status-text small {
  font-size: 12px;
  opacity: 0.8;
}

.action-btn {
  border-radius: 8px;
  font-weight: 600;
  text-transform: none;
}

.empty-state {
  border-radius: 12px;
  margin-top: 40px;
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 960px) {
  .info-grid {
    grid-template-columns: 1fr;
  }

  .card-header {
    flex-direction: column;
    gap: 12px;
    text-align: center;
  }

  .action-section {
    margin-top: 20px;
  }
}
</style>
